// src/app/client-layout.tsx

"use client";

import { AntdRegistry } from "@ant-design/nextjs-registry";
import Providers from "./providers";
import "@fontsource/inter";
import { App as AntdApp, ConfigProvider, theme } from "antd";
import "antd/dist/reset.css";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useEffect } from "react";

// ============================================
// STRIPE-INSPIRED ANT DESIGN THEME
// ============================================

const brandColors = {
  // Primary Teal/Cyan
  primary: "#0d9488", // brand-600
  primaryHover: "#0f766e", // brand-700
  primaryActive: "#115e59", // brand-800
  primaryLight: "#14b8a6", // brand-500
  primaryBg: "#f0fdfa", // brand-50
  primaryBgHover: "#ccfbf1", // brand-100

  // Navy for text
  navy: "#102a43", // navy-900
  navyLight: "#334e68", // navy-700
  navyMid: "#627d98", // navy-500

  // Semantic
  success: "#10b981",
  warning: "#f59e0b",
  error: "#ef4444",
  info: "#3b82f6",
};

const antdTheme = {
  token: {
    // ============================================
    // COLORS
    // ============================================
    colorPrimary: brandColors.primary,
    colorPrimaryHover: brandColors.primaryHover,
    colorPrimaryActive: brandColors.primaryActive,
    colorPrimaryBg: brandColors.primaryBg,
    colorPrimaryBgHover: brandColors.primaryBgHover,
    colorLink: brandColors.primary,
    colorLinkHover: brandColors.primaryHover,
    colorLinkActive: brandColors.primaryActive,
    colorSuccess: brandColors.success,
    colorWarning: brandColors.warning,
    colorError: brandColors.error,
    colorInfo: brandColors.info,

    // Text colors
    colorTextBase: brandColors.navy,
    colorText: brandColors.navyLight,
    colorTextSecondary: brandColors.navyMid,
    colorTextTertiary: "#829ab1",
    colorTextQuaternary: "#9fb3c8",

    // Background colors
    colorBgContainer: "#ffffff",
    colorBgLayout: "#fafbfc",
    colorBgElevated: "#ffffff",
    colorBgSpotlight: "#f0fdfa",

    // Border colors
    colorBorder: "#d9e2ec",
    colorBorderSecondary: "#e4e9ef",

    // ============================================
    // TYPOGRAPHY
    // ============================================
    fontFamily: '"Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 14,
    fontSizeSM: 12,
    fontSizeLG: 16,
    fontSizeXL: 20,
    fontSizeHeading1: 38,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,
    fontWeightStrong: 600,
    lineHeight: 1.5714285714285714,
    lineHeightLG: 1.5,
    lineHeightSM: 1.6666666666666667,
    lineHeightHeading1: 1.2105263157894737,
    lineHeightHeading2: 1.2666666666666666,
    lineHeightHeading3: 1.3333333333333333,
    lineHeightHeading4: 1.4,
    lineHeightHeading5: 1.5,

    // ============================================
    // BORDER RADIUS
    // ============================================
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    borderRadiusXS: 4,

    // ============================================
    // SHADOWS - Stripe-inspired layered shadows
    // ============================================
    boxShadow: "0 2px 4px rgba(10, 37, 64, 0.04), 0 8px 16px rgba(10, 37, 64, 0.08)",
    boxShadowSecondary: "0 4px 8px rgba(10, 37, 64, 0.06), 0 16px 32px rgba(10, 37, 64, 0.12)",
    boxShadowTertiary: "0 1px 2px rgba(10, 37, 64, 0.08)",

    // ============================================
    // SPACING
    // ============================================
    padding: 16,
    paddingLG: 24,
    paddingMD: 20,
    paddingSM: 12,
    paddingXS: 8,
    paddingXXS: 4,
    margin: 16,
    marginLG: 24,
    marginMD: 20,
    marginSM: 12,
    marginXS: 8,
    marginXXS: 4,

    // ============================================
    // CONTROL SIZES - Larger for better touch targets
    // ============================================
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,
    controlHeightXS: 24,

    // ============================================
    // MOTION
    // ============================================
    motionDurationFast: "0.15s",
    motionDurationMid: "0.25s",
    motionDurationSlow: "0.35s",
    motionEaseInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    motionEaseOut: "cubic-bezier(0, 0, 0.2, 1)",
    motionEaseOutBack: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  },

  // ============================================
  // COMPONENT-LEVEL CUSTOMIZATIONS
  // ============================================
  components: {
    Button: {
      primaryShadow: "0 1px 2px rgba(10, 37, 64, 0.08), 0 2px 4px rgba(10, 37, 64, 0.06)",
      defaultShadow: "0 1px 2px rgba(10, 37, 64, 0.05)",
      fontWeight: 500,
      paddingInline: 20,
      paddingBlock: 8,
      borderRadius: 8,
      controlHeight: 42,
      controlHeightLG: 50,
      controlHeightSM: 34,
    },
    Card: {
      borderRadiusLG: 16,
      paddingLG: 24,
      boxShadow: "0 2px 4px rgba(10, 37, 64, 0.04), 0 8px 16px rgba(10, 37, 64, 0.08)",
      headerFontSize: 18,
      headerFontSizeSM: 16,
    },
    Input: {
      borderRadius: 8,
      paddingInline: 14,
      paddingBlock: 10,
      controlHeight: 42,
      controlHeightLG: 50,
      controlHeightSM: 34,
      activeShadow: "0 0 0 3px rgba(20, 184, 166, 0.15)",
      hoverBorderColor: brandColors.primaryLight,
    },
    InputNumber: {
      borderRadius: 8,
      controlHeight: 42,
      activeShadow: "0 0 0 3px rgba(20, 184, 166, 0.15)",
    },
    Select: {
      borderRadius: 8,
      controlHeight: 42,
      controlHeightLG: 50,
      controlHeightSM: 34,
      optionPadding: "10px 14px",
    },
    Modal: {
      borderRadiusLG: 16,
      paddingContentHorizontalLG: 32,
      paddingMD: 24,
      titleFontSize: 20,
      headerBg: "#ffffff",
    },
    Drawer: {
      borderRadiusLG: 16,
      paddingLG: 24,
    },
    Table: {
      borderRadius: 12,
      headerBg: "#f8fafc",
      headerColor: "#334e68",
      rowHoverBg: "#f0fdfa",
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
    },
    Tabs: {
      inkBarColor: brandColors.primary,
      itemActiveColor: brandColors.primary,
      itemHoverColor: brandColors.primaryLight,
      itemSelectedColor: brandColors.primary,
      titleFontSize: 15,
      horizontalItemPadding: "12px 16px",
    },
    Tag: {
      borderRadiusSM: 6,
      defaultBg: "#f0fdfa",
      defaultColor: brandColors.primary,
    },
    Result: {
      titleFontSize: 24,
      subtitleFontSize: 16,
    },
    Steps: {
      iconSize: 32,
      iconFontSize: 14,
      titleLineHeight: 1.5,
      dotSize: 8,
    },
    Progress: {
      defaultColor: brandColors.primary,
      remainingColor: "#e4e9ef",
      circleTextFontSize: "1em",
    },
    Spin: {
      colorPrimary: brandColors.primary,
    },
    Tooltip: {
      borderRadius: 8,
      colorBgSpotlight: "#102a43",
    },
    Message: {
      borderRadiusLG: 10,
    },
    Notification: {
      borderRadiusLG: 12,
    },
    Menu: {
      itemBorderRadius: 8,
      subMenuItemBorderRadius: 8,
      itemHoverBg: "#f0fdfa",
      itemSelectedBg: "#ccfbf1",
      itemSelectedColor: brandColors.primary,
    },
    Dropdown: {
      borderRadiusLG: 10,
      paddingBlock: 6,
    },
    Collapse: {
      borderRadiusLG: 12,
      headerBg: "#fafbfc",
    },
    Alert: {
      borderRadiusLG: 10,
    },
    Badge: {
      textFontSize: 11,
      textFontWeight: 600,
    },
    Avatar: {
      borderRadius: 8,
    },
    Breadcrumb: {
      itemColor: "#627d98",
      linkColor: brandColors.primary,
      linkHoverColor: brandColors.primaryHover,
    },
    Checkbox: {
      borderRadiusSM: 4,
    },
    Radio: {
      buttonSolidCheckedBg: brandColors.primary,
    },
    Switch: {
      colorPrimary: brandColors.primary,
      colorPrimaryHover: brandColors.primaryHover,
    },
    Slider: {
      trackBg: "#e4e9ef",
      handleColor: brandColors.primary,
      dotActiveBorderColor: brandColors.primary,
    },
    DatePicker: {
      borderRadius: 8,
      controlHeight: 42,
      activeShadow: "0 0 0 3px rgba(20, 184, 166, 0.15)",
    },
    Upload: {
      borderRadiusLG: 12,
    },
    Form: {
      itemMarginBottom: 24,
      verticalLabelPadding: "0 0 8px",
    },
    Segmented: {
      borderRadius: 8,
      itemSelectedBg: "#ffffff",
    },
  },
  algorithm: theme.defaultAlgorithm,
};

// Route Analytics Component
function RouteAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = searchParams?.toString() ?? "";
    const page_path = sp ? `${pathname}?${sp}` : pathname;
    const page_location = window.location.href;
    const page_title = document.title;

    // GA4 via gtag (if present)
    const gaId = process.env.NEXT_PUBLIC_GA_ID;
    const gtag = (window as any).gtag as undefined | ((...args: any[]) => void);
    if (gtag && gaId) {
      gtag("event", "page_view", {
        page_title,
        page_location,
        page_path,
        send_to: gaId,
      });
    }

    // GTM dataLayer (if present) for SPA pageviews
    const dl = (window as any).dataLayer;
    if (Array.isArray(dl)) {
      dl.push({
        event: "pageview",
        page_title,
        page_location,
        page_path,
      });
    }
  }, [pathname, searchParams]);

  return null;
}

export default function ClientLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <AntdRegistry>
        <ConfigProvider theme={antdTheme}>
          <Providers>
            <AntdApp>
              <Suspense fallback={null}>
                <RouteAnalytics />
              </Suspense>
              {children}
            </AntdApp>
          </Providers>
        </ConfigProvider>
      </AntdRegistry>
    </>
  );
}
